import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ClientPortalApiService } from '../../services/client-portal-api.service';
import { AuthStateService } from '@hypenation/auth';
import { OrganiserDto, OrganiserPublicProfileResponse } from '../../models/client-portal.models';
import { EventModel } from '@hypenation/tickets';

@Component({
  selector: 'hypenation-organiser-profile',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzSpinModule,
    NzIconModule,
    NzButtonModule,
    NzEmptyModule,
  ],
  templateUrl: './organiser-profile.component.html',
  styleUrl: './organiser-profile.component.css',
})
export class OrganiserProfileComponent implements OnInit {
  loading = false;
  organiser: OrganiserDto | null = null;
  upcomingEvents: EventModel[] = [];
  organiserEvents: EventModel[] = [];
  followerCount = 0;
  isFavourite = false;
  favouriteLoading = false;

  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ClientPortalApiService);
  private readonly authStateService = inject(AuthStateService);
  private readonly notification = inject(NzNotificationService);

  private get userEmail(): string | null {
    return this.authStateService.getUserEmail();
  }

  ngOnInit(): void {
    const uid = this.route.snapshot.paramMap.get('uid');
    if (uid) {
      this.loadProfile(uid);
      this.loadOrganiserEvents(uid);
      this.checkFavourite(uid);
    }
  }

  loadProfile(uid: string): void {
    this.loading = true;
    this.apiService.getOrganiserProfile(uid).subscribe({
      next: (res) => {
        const data = res.body;
        if (data) {
          this.organiser = data.organiser;
          this.upcomingEvents = data.upcomingEvents ?? [];
          this.followerCount = data.followerCount ?? 0;
        }
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  loadOrganiserEvents(uid: string): void {
    this.apiService.getOrganiserEvents(uid).subscribe({
      next: (res) => {
        this.organiserEvents = res.body ?? [];
      },
    });
  }

  checkFavourite(uid: string): void {
    if (!this.userEmail) return;
    this.apiService.isOrganiserFavourite(uid, this.userEmail).subscribe({
      next: (res) => {
        this.isFavourite = res.body === true;
      },
    });
  }

  toggleFavourite(): void {
    if (!this.organiser || !this.userEmail) return;

    this.favouriteLoading = true;
    if (this.isFavourite) {
      this.apiService.removeFavouriteOrganiser(this.organiser.uid, this.userEmail).subscribe({
        next: () => {
          this.isFavourite = false;
          this.followerCount = Math.max(0, this.followerCount - 1);
          this.favouriteLoading = false;
          this.notification.success('Unfollowed', `You unfollowed ${this.organiser!.name}.`);
        },
        error: () => (this.favouriteLoading = false),
      });
    } else {
      this.apiService.addFavouriteOrganiser(this.organiser.uid, this.userEmail).subscribe({
        next: () => {
          this.isFavourite = true;
          this.followerCount++;
          this.favouriteLoading = false;
          this.notification.success('Following', `You are now following ${this.organiser!.name}.`);
        },
        error: () => (this.favouriteLoading = false),
      });
    }
  }

  getInitial(name: string): string {
    return name ? name.charAt(0).toUpperCase() : '?';
  }
}
